import {
  ActivityIndicator, Pressable, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import React, {
  Dispatch, SetStateAction, useEffect, useRef, useState,
} from 'react';
import Moment from 'moment';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  SharedValue,
  useAnimatedReaction,
  useAnimatedStyle, useSharedValue,
} from 'react-native-reanimated';
import { useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet';
import Colors from '../../constants/Colors';
import Style from '../../constants/Style';
import MoveIcon from '../16pxIcons/MoveIcon';
import updateAsset from '../../requests/Assets/UpdateAsset';
import isMobile from '../IsMobile';
import TickIcon from '../TickIcon';
import DeleteIcon12 from '../12pxIcons/DeleteIcon12';
import IEQIcon from '../24pxIcons/IEQIcon';
import MeterIcon from '../24pxIcons/MeterIcon';
import { SAVE_POSITION_SUCCESS, SOMETHING_WENT_WRONG } from '../../constants/AlertMessages';
import PersonIcon from '../PersonIcon';
import DoorIcon from '../DoorIcon';
import { getLocationById } from '../../constants/Utils';
import { Area, Space } from './SetFloorsModal/FloorItem';
import EllipsisIconVertical from '../EllipsisIconVertical';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import RightArrow14 from '../14pxIcons/RightArrow14';
import { Metric } from '../../constants/Types';
import { AvailableMetrics } from '../../requests/Sensors/GetAvailableMetrics';

interface Props {
  sensor?: any;
  movingAssetId?: string;
  setMovingItem?: any;
  showAlert?: any;
  isPointInShape: any;
  negativeScale?: number;
  mapViewWidth?: number;
  mapViewHeight?: number;
  sensorHovered?: boolean;
  scale?: number;
  focusedAssetId?: SharedValue<string | null>;
  editMode?: boolean;
  setSensorMenuOpen?: Dispatch<SetStateAction<string | null>>;
  sensorMenuOpen?: string | null;
  editSensorPosition?: () => void;
  deleteSensorPosition?: () => void;
  floorId?: string;
  availableMetrics?: AvailableMetrics | null;
  modalVisible?: boolean;
  setModalVisible?: Dispatch<SetStateAction<boolean>>;
  isSelected?: boolean;
}

const Sensor = ({
  sensor, movingAssetId, isPointInShape, sensorHovered, scale, editMode,
  setMovingItem, showAlert, mapViewWidth, mapViewHeight, negativeScale, focusedAssetId,
  setSensorMenuOpen, sensorMenuOpen, editSensorPosition, deleteSensorPosition, floorId,
  availableMetrics, modalVisible, setModalVisible, isSelected,
}: Props) => {
  const navigation = useNavigation();
  const bottomSheetModalRef = useRef<BottomSheetModal>(null);

  const translateXsen = useSharedValue<number>(sensor.GeoX);
  const translateYsen = useSharedValue<number>(sensor.GeoY);

  const movingItem = movingAssetId === sensor.id;

  const {
    temp, co2, humidity, lux, pm10, pm25, spl, tvoc, type, name,
  } = sensor;

  const [loading, setLoading] = useState(false);
  const [optionsShown, setOptionsShown] = useState<boolean>(false);
  // Use parent modalVisible/setModalVisible if provided
  const [isFocused, setIsFocused] = useState(false);

  const left = useSharedValue(0);
  const top = useSharedValue(0);
  const align = useSharedValue<'flex-start' | 'flex-end'>('flex-start');

  const getTopOffsetForSensorDialog = (): number => {
    switch (type) {
      case 'pir':
        return -114;
      case 'ent':
        return -20;
      case 'water':
      case 'electric':
      case 'pcs':
        return -170;
      default:
        return -320;
    }
  };

  const isMetricEnabled = (metric: Metric): boolean => availableMetrics?.[metric] !== false;

  const buildings: object = useSelector((state:
    { locations: { buildings: object } }) => state.locations.buildings);
  const sensorLocation = getLocationById(sensor.locationId, buildings);
  const sensorLocationPoints = (sensorLocation as Area | Space)?.Points?.map((point) => {
    const transformedPoint = { ...point };
    Object.keys(point).forEach((pointKey: string) => {
      transformedPoint[pointKey.toLowerCase()] = point[pointKey];
    });
    return transformedPoint;
  });

  useAnimatedReaction(
    () => focusedAssetId?.value === sensor.id,
    (focusedValue, previousFocusedValue) => {
      if (focusedValue !== previousFocusedValue && sensorMenuOpen !== sensor.id) {
        runOnJS(setIsFocused)(focusedValue);
      }
    },
  );

  useEffect(() => {
    if (modalVisible && isSelected) {
      bottomSheetModalRef.current?.present();
    } else {
      bottomSheetModalRef.current?.dismiss();
    }
  }, [modalVisible, isSelected]);

  function sensorMoveEnd() {
    if (!sensorLocationPoints
      || isPointInShape(translateXsen.value, translateYsen.value, sensorLocationPoints)) {
      setOptionsShown(true);
      setMovingItem(true);
    } else setOptionsShown(false);
  }

  const panGestureSen = Gesture.Pan()
    .onChange((event) => {
      translateXsen.value += event.changeX / scale;
      translateYsen.value += event.changeY / scale;
    })
    .onEnd(() => sensorMoveEnd());

  const save: () => void = () => {
    if (isPointInShape(translateXsen.value, translateYsen.value)) {
      setLoading(true);
      const updatedCustomFields = sensor.customFields?.map((field) => ({ ...field }))
        ?.filter((field) => !(
          field.name?.toLowerCase() === 'geox' || field.name?.toLowerCase() === 'geoy'
        )) ?? [];
      updatedCustomFields.push({ name: 'GeoX', value: `${translateXsen.value}` });
      updatedCustomFields.push({ name: 'GeoY', value: `${translateYsen.value}` });
      updateAsset({
        assetId: sensor.id,
        name: sensor.name,
        description: sensor.description,
        manufacturer: sensor.manufacturer,
        model: sensor.model,
        serial: sensor.serial,
        locationId: sensor.locationId,
        customFields: updatedCustomFields,
        labels: sensor.labels,
      }).then((response) => {
        const success = response && !response.isError;
        if (!success) {
          showAlert(SOMETHING_WENT_WRONG, true);
        } else {
          showAlert(SAVE_POSITION_SUCCESS, false);
          sensor.GeoX = translateXsen.value;
          sensor.GeoY = translateYsen.value;
          sensor.customFields = updatedCustomFields;
        }
        setMovingItem(false);
        setLoading(false);
      });
    }
  };

  const cancel: () => void = () => {
    translateXsen.value = sensor.GeoX;
    translateYsen.value = sensor.GeoY;
    if (sensor.GeoX === 20 && sensor.GeoY === 120) {
      sensor.hasXY = false;
      sensor.GeoX = 0;
      sensor.GeoY = 0;
    }
    setOptionsShown(false);
    setMovingItem(false);
  };

  const transformStyleSen = useAnimatedStyle(() => ({
    // @ts-ignore
    transform: [
      { translateX: translateXsen.value },
      { translateY: translateYsen.value },
    ],
  }), [sensor.id]);

  const opacityStyle = useAnimatedStyle(() => {
    const baseOffset = 30;
    const bridgePadding = 8;
    const dialogOnLeft = left.value <= -300;
    const dialogOnTop = align.value === 'flex-end';
    const paddingLeft = dialogOnLeft ? 0 : bridgePadding;
    const paddingRight = dialogOnLeft ? bridgePadding : 0;
    const topBridgeOffset = dialogOnTop ? 20 : 0;

    return {
      opacity: 1,
      height: undefined,
      overflow: 'visible',
      left: left.value + baseOffset - paddingLeft,
      top: top.value + topBridgeOffset,
      position: 'absolute',
      justifyContent: align.value,
      paddingLeft,
      paddingRight,
      paddingBottom: 0,
    };
  }, [sensor.id]);

  const innerColors: string[] = [
    Colors.light.hereworksGreen400,
    Colors.light.hereworksYellow500,
    Colors.light.hereworksRed500,
  ];

  const outerColors: string[] = [
    Colors.light.hereworksGreen50,
    Colors.light.hereworksYellow50,
    Colors.light.hereworksRed50,
  ];

  const getSensorColor = () => {
    if (sensor.type === 'pir') return 'transparent';
    if (sensor.type === 'pcs') return Colors.light.hereworksGreen400;
    if (!sensor.timestamp) return Colors.light.hereworksBlack100;
    if ((isMetricEnabled(Metric.TEMPERATURE) && (temp < 18 || temp > 23))
      || (isMetricEnabled(Metric.CO2) && co2 > 2000)
      || (isMetricEnabled(Metric.NOISE) && spl > 95)
      || (isMetricEnabled(Metric.HUMIDITY) && humidity > 80)
      || (isMetricEnabled(Metric.PM25) && pm25 > 35.5)
      || (isMetricEnabled(Metric.TVOC) && tvoc > 660)) {
      return Colors.light.hereworksRed500;
    }
    if ((isMetricEnabled(Metric.CO2) && co2 > 1000)
      || (isMetricEnabled(Metric.NOISE) && spl > 60)
      || (isMetricEnabled(Metric.TVOC) && tvoc > 220)
      || (isMetricEnabled(Metric.HUMIDITY) && humidity > 60)
      || (isMetricEnabled(Metric.PM25) && pm25 > 12.1)) return Colors.light.hereworksYellow500;

    return Colors.light.hereworksGreen400;
  };

  const getSensorFillColor = () => {
    if (sensor.type === 'pir') {
      if (!sensor.lastDetected) return Colors.light.hereworksBlack300;
      if (sensor.motionDetected) return Colors.light.hereworksRed500;
      return Colors.light.hereworksGreen500;
    }
    if (!sensor.timestamp) return Colors.light.hereworksBlack300;

    return '#fff';
  };

  const getSensorBorderColor = () => {
    if (sensor.type === 'pir') return 'transparent';
    if (sensor.type === 'pcs') return Colors.light.hereworksGreen400;
    if (!sensor.timestamp) return Colors.light.hereworksBlack300;

    return getSensorColor();
  };

  const getSecondarySensorColor = () => {
    if ((isMetricEnabled(Metric.TEMPERATURE) && (temp < 18 || temp > 23))
      || (isMetricEnabled(Metric.CO2) && co2 > 1500)
      || (isMetricEnabled(Metric.NOISE) && spl > 95)
      || (isMetricEnabled(Metric.HUMIDITY) && humidity > 60)
      || (isMetricEnabled(Metric.PM25) && pm25 > 35.5)
      || (isMetricEnabled(Metric.TVOC) && tvoc > 660)) {
      return Colors.light.hereworksRed50;
    }
    if ((isMetricEnabled(Metric.CO2) && co2 > 1000)
      || (isMetricEnabled(Metric.NOISE) && spl > 60)
      || (isMetricEnabled(Metric.TVOC) && tvoc > 220)) return Colors.light.hereworksYellow50;

    return Colors.light.hereworksGreen50;
  };

  const getInnerColor = (below, moderate, high, value) => {
    if (below !== null && value < below) return innerColors[1];
    if (high !== null && value > high) return innerColors[2];
    if (moderate && value > moderate) return innerColors[1];
    return innerColors[0];
  };

  const getStatusText = () => {
    if (sensor.type === 'pir') {
      if (!sensor.lastDetected) return 'No activity for at least 3 days';
      return sensor.motionDetected ? 'Motion Detected' : 'Clear';
    }
    return sensor.timestamp ? 'Online' : 'Offline';
  };

  const getOuterColor = (below, moderate, high, value) => {
    if (below !== null && value < below) return outerColors[1];
    if (value > high) return outerColors[2];
    if (moderate && value > moderate) return outerColors[1];
    return outerColors[0];
  };

  const circle = (innerColor, outerColor) => (
    <View
      style={{
        width: 16,
        height: 16,
        borderRadius: 8,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: outerColor,
      }}
    >
      <View
        style={{
          width: 8,
          height: 8,
          borderRadius: 4,
          backgroundColor: innerColor,
        }}
      />
    </View>
  );

  const sensorRow = (belowThreshold, moderate, high, title, desc, value) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
      {circle(
        getInnerColor(belowThreshold, moderate, high, value),
        getOuterColor(belowThreshold, moderate, high, value),
      )}
      <Text style={styles.sensorRowTitle}>{title}</Text>
      <Text style={styles.sensorRowValue}>{desc}</Text>
    </View>
  );

  const goToAsset: () => void = () => {
    setModalVisible(false);
    navigation.navigate(AppScreen.ASSET_MANAGEMENT as any, { assetId: sensor.id });
    // TODO: remove following block after testing
    // navigation.navigate(AppScreen.TEMPLATE_MANAGEMENT as any, {
    //   assetId: sensor.id ?? '-',
    //   templateId: sensor.templateId,
    // });
  };

  const SensorActionButtons = ({ onSeeDetails }: { onSeeDetails: () => void }) => (
    <View style={styles.sensorActionRow}>
      <TouchableOpacity
        style={[styles.detailsButton, styles.sensorActionButton]}
        onPress={onSeeDetails}
        testID='navigate-to-sensor-btn'
      >
        <Text style={styles.detailsButtonTitle}>View Insights</Text>
      </TouchableOpacity>
    </View>
  );

  const PcsInfo = () => (
    <>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        {circle(getSensorColor(), getSecondarySensorColor())}
        <Text style={styles.buttonText}>{sensor.name}</Text>
        <Text selectable>{sensor.id}</Text>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
        <Text
          style={{
            fontFamily: 'Poppins_400Regular',
            fontSize: 12,
            color: Colors.light.hereworksBlack400,
          }}
        >
          Last reading:
        </Text>
        <Text style={styles.timeText}>
          {sensor.timestamp ? Moment(sensor.timestamp).format('h:mm a, D MMM') : '-'}
        </Text>
      </View>
      <SensorActionButtons
        onSeeDetails={() => {
          setModalVisible(false);
          navigation.navigate(AppScreen.OCCUPANCY as any, { assetId: sensor.id ?? '' });
        }}
      />
    </>
  );

  const IeqInfo = () => (
    <>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        {circle(getSensorColor(), getSecondarySensorColor())}
        <Text style={styles.buttonText}>{sensor.name}</Text>
      </View>
      <View style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginVertical: 16,
      }}>
        <Text style={{
          fontFamily: 'Poppins_400Regular',
          fontSize: 12,
          color: Colors.light.hereworksBlack400,
        }}>Asset ID: </Text>
        <TouchableOpacity
          onPress={goToAsset}
          style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}
        >
          <Text
            selectable
            style={{
              fontFamily: 'Poppins_400Regular',
              fontSize: 12,
              color: Colors.light.hereworksBlue500,
            }}
            numberOfLines={1}
            ellipsizeMode='clip'
          >
            {sensor.id}
          </Text>
          <RightArrow14 />
        </TouchableOpacity>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
        <Text
          style={{
            fontFamily: 'Poppins_400Regular',
            fontSize: 12,
            color: Colors.light.hereworksBlack400,
          }}
        >
          Last reading:
        </Text>
        <Text style={styles.timeText}>
          {sensor.timestamp ? Moment(sensor.timestamp).format('h:mm a, D MMM') : '-'}
        </Text>
      </View>
      {isMetricEnabled(Metric.TEMPERATURE)
        ? sensorRow(18, null, 23, 'Temperature', `${temp} °C`, temp) : null}
      {isMetricEnabled(Metric.HUMIDITY)
        ? sensorRow(null, 60, 80, 'Humidity', `${humidity}%`, humidity) : null}
      {isMetricEnabled(Metric.CO2)
        ? sensorRow(null, 1000, 1500, 'CO₂', `${co2} ppm`, co2) : null}

      {isMetricEnabled(Metric.TVOC)
        ? sensorRow(null, 220, 660, 'TVOCs', `${tvoc} ppb`, tvoc) : null}
      {(isMetricEnabled(Metric.PM10) && pm10)
        ? sensorRow(null, 54, 154, 'PM 10', `${pm10} µg/m³`, pm10) : null}
      {isMetricEnabled(Metric.PM25)
        ? sensorRow(null, 12.1, 35.5, 'PM 2.5', `${pm25} µg/m³`, pm25) : null}
      {(isMetricEnabled(Metric.NOISE) && spl)
        ? sensorRow(null, 60, 80, 'Noise', `${spl} dB`, spl) : null}
      {(isMetricEnabled(Metric.LIGHT) && lux)
        ? sensorRow(null, null, null, 'Light', `${lux} lx`, lux) : null}
      <SensorActionButtons
        onSeeDetails={() => {
          setModalVisible(false);
          navigation.navigate(AppScreen.INTERNAL_ENVIRONMENT as any, {
            assetId: sensor.id ?? '',
            sensorName: sensor.name,
            floorId,
            areaId: sensor.locationId,
            templateId: sensor.templateId,
          });
        }}
      />
    </>
  );

  const WaterInfo = () => (
    <View testID='map-meter-sensor-details'>
      <Text style={styles.buttonText}>{name}</Text>
      <Text style={styles.buttonText}>Last meter reading: {sensor.waterUsage}</Text>
      <Text style={styles.buttonText}>Timestamp: {sensor.timestamp ? Moment(sensor.timestamp).format('h:mm a, D MMM') : '-'}</Text>
      <SensorActionButtons
        onSeeDetails={() => {
          setModalVisible(false);
          navigation.navigate(AppScreen.ENERGY as any, { waterMeterId: sensor.id ?? '' });
        }}
      />
    </View>
  );

  const ElectricInfo = () => (
    <View testID='map-meter-sensor-details'>
      <Text style={styles.buttonText}>{name}</Text>
      <Text style={styles.buttonText}>Last meter reading: {sensor.energyUsage}</Text>
      <Text style={styles.buttonText}>Timestamp: {sensor.timestamp ? Moment(sensor.timestamp).format('h:mm a, D MMM') : '-'}</Text>
      <SensorActionButtons
        onSeeDetails={() => {
          setModalVisible(false);
          navigation.navigate(AppScreen.ENERGY as any, { elecMeterId: sensor.id ?? '' });
        }}
      />
    </View>
  );

  const EntInfo = () => (
    <View testID='map-ent-sensor-details'>
      <Text style={styles.buttonText}>{name}</Text>
    </View>
  );

  const PirInfo = () => (
    <View testID='map-pir-sensor-details'>
      <Text style={[styles.detailHeading, { marginTop: 0 }]}>{name}</Text>
      <Text style={[styles.timeText, { textAlign: 'left', color: Colors.light.hereworksBlack400 }]}>
        {`ID: ${sensor.id}`}
      </Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 12 }}>
        <Text style={[styles.sensorRowTitle, { marginLeft: 0, marginRight: 8 }]}>Status:</Text>
        {circle(
          getSensorFillColor(),
          getSecondarySensorColor(),
        )}
        <Text style={[styles.pirValue, { marginLeft: 4 }]}>
          {getStatusText()}
        </Text>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text style={[styles.sensorRowTitle, { marginLeft: 0 }]}>
          {'Last detected: '}
        </Text>
        <Text style={[styles.pirValue, { marginLeft: 6 }]}>{sensor.lastDetected ?? '-'}</Text>
      </View>
    </View>
  );

  const sensorData = () => {
    switch (type) {
      case 'water': return <WaterInfo />;
      case 'electric': return <ElectricInfo />;
      case 'pir': return <PirInfo />;
      case 'pcs': return <PcsInfo />;
      case 'ent': return <EntInfo />;
      default: return <IeqInfo />;
    }
  };

  const sensorIcon = () => {
    switch (type) {
      case 'water':
      case 'electric':
        return <MeterIcon
          stroke={getSensorFillColor()}
          testID='map-meter-sensor-icon'
        />;
      case 'pir': return <View
        style={[styles.pir, { borderColor: getSensorFillColor() }]}
        testID='map-pir-sensor-icon'
      >
        <View style={{
          width: 10, height: 10, borderRadius: 5, backgroundColor: getSensorFillColor(),
        }} />
      </View>;
      case 'pcs': return <PersonIcon color={Colors.light.plainBackground} fill={Colors.light.hereworksGreen400} />;
      case 'ent': return <DoorIcon color={getSensorFillColor()} fill={getSensorFillColor()} />;
      default: return <IEQIcon
        fill={getSensorFillColor()}
        testID='map-ieq-sensor-icon'
      />;
    }
  };

  const SensorInfo = () => (
    <Animated.View
      style={[isMobile ? {
        padding: 0,
        flex: 1,
        justifyContent: 'flex-end',
        alignItems: 'center',
        alignSelf: 'stretch',
      } : opacityStyle]}
      testID='map-sensor-details-modal'
    >
      <View
        style={[Style.boxShadow, styles.sensorInfo, type === 'pir' && styles.pirBox]}>
        {sensorData()}
        {editMode && <TouchableOpacity
          onPress={() => {
            if (sensorMenuOpen === sensor.id) setSensorMenuOpen(null);
            else setSensorMenuOpen(sensor.id);
          }}
          style={{ position: 'absolute', top: 16, right: 12 }}>
          <EllipsisIconVertical />
        </TouchableOpacity>}
        {(sensorMenuOpen === sensor.id && editMode) && (
          <View
            style={{
              position: 'absolute',
              top: 40,
              right: -60,
              backgroundColor: '#fff',
              borderRadius: 8,
              borderColor: Colors.light.hereworksBlue200,
              borderWidth: 1,
              zIndex: 4,
            }}
          >
            <TouchableOpacity
              onPress={() => {
                setSensorMenuOpen(null);
                editSensorPosition();
              }}
              testID='map-edit-area-modal-btn'
            >
              <Text style={styles.menuText}>
                Edit Position
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => {
                setSensorMenuOpen(null);
                deleteSensorPosition();
              }}
            >
              <Text style={styles.menuText}>
                Delete Position
              </Text>
            </TouchableOpacity>
            {loading && (
              <View
                style={{
                  position: 'absolute',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#00000040',
                }}
              >
                <ActivityIndicator animating={true} />
              </View>
            )}
          </View>
        )}
      </View>
    </Animated.View>
  );

  const SensorIconContainer = () => <View
    style={[
      {
        backgroundColor: getSensorColor(),
        width: 24,
        height: 24,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: type === 'pir' ? 12 : 4,
        borderWidth: 1,
        borderColor: getSensorBorderColor(),
        transform: [{ scale: type === 'pir' ? 1 : 1.3 }],
      },
      styles.shadow,
    ]}
  >
    {sensorIcon()}
  </View>;

  if (isMobile) {
    return (
      <>
        <SensorIconContainer />
        <BottomSheetModal
          ref={bottomSheetModalRef}
          onDismiss={() => setModalVisible && setModalVisible(false)}
          backdropComponent={(props) => (
            <BottomSheetBackdrop
              {...props}
              disappearsOnIndex={-1}
              appearsOnIndex={0}
            />
          )}
        >
          <BottomSheetView style={{ flex: 1 }}>
            <SensorInfo />
          </BottomSheetView>
        </BottomSheetModal>
      </>
    );
  }

  return (
    <View
      testID='map-sensor-icon'
      key={`${sensor.id}`}
      style={[
        {
          position: 'absolute',
          zIndex: sensorHovered ? 60 : 1,
          top: 0,
          left: 0,
        },
      ]}
    >
      <View style={{ zIndex: sensorHovered ? 60 : 1 }}>
        <Animated.View style={transformStyleSen}>
          <View
            style={{ position: 'absolute', transform: [{ scale: negativeScale }] }}
            onPointerEnter={(event) => {
              if (isFocused) return;
              if ((event.nativeEvent.x - 300) > mapViewWidth / 2) left.value = -312;
              else left.value = -2;
              if ((event.nativeEvent.y) > mapViewHeight) {
                top.value = getTopOffsetForSensorDialog();
                align.value = 'flex-start';
              } else if ((event.nativeEvent.y - 150) > mapViewHeight / 2) {
                top.value = -200;
                align.value = 'flex-end';
              } else {
                top.value = -4;
                align.value = 'flex-start';
              }
            }}
          >
            {!movingItem && (
              <View style={[{ position: 'absolute' }]}>
                <>
                  <SensorIconContainer />
                  {isFocused && <SensorInfo />}
                  {/* {sensorInfo()} for testing */}
                </>
              </View>
            )}
            {movingItem && (
              <View style={{ position: 'absolute' }}>
                <SensorIconContainer />
                <GestureDetector gesture={panGestureSen}>
                  <Pressable
                    style={(state) => [
                      styles.icon,
                      {
                        opacity: movingItem ? 1 : 0,
                        zIndex: state.hovered ? 10 : 1,
                        borderColor: state.hovered
                          ? Colors.light.hereworksBlue500
                          : Colors.light.hereworksBlue100,
                      },
                    ]}
                    disabled={!movingItem}
                  >
                    <MoveIcon />
                    {loading && (
                      <View style={styles.loader}>
                        <ActivityIndicator animating={true} />
                      </View>
                    )}
                  </Pressable>
                </GestureDetector>
                {optionsShown && !loading && <>
                  <Pressable
                    style={(state: { hovered: boolean, pressed: boolean }) => [
                      styles.icon, styles.tickIcon,
                      {
                        opacity: movingItem ? 1 : 0,
                        zIndex: state.hovered ? 10 : 1,
                        borderColor: state.hovered
                          ? Colors.light.hereworksBlue500
                          : Colors.light.hereworksBlue100,
                      },
                    ]}
                    disabled={!movingItem}
                    onPress={save}
                  >
                    <TickIcon />
                  </Pressable>
                  <Pressable
                    style={(state: { hovered: boolean, pressed: boolean }) => [
                      styles.icon, styles.deleteIcon,
                      {
                        opacity: movingItem ? 1 : 0,
                        zIndex: state.hovered ? 10 : 1,
                        borderColor: state.hovered
                          ? Colors.light.hereworksRed500
                          : Colors.light.hereworksRed300,
                      },
                    ]}
                    disabled={!movingItem}
                    onPress={cancel}
                  >
                    <DeleteIcon12 color={Colors.light.hereworksRed500} />
                  </Pressable>
                </>}
              </View>
            )}
          </View>
        </Animated.View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  sensorRowTitle: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 22,
    color: Colors.light.hereworksBlack400,
    marginLeft: 12,
  },
  sensorRowValue: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    textAlign: 'right',
    flex: 1,
  },
  pirValue: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
  },
  buttonText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 16,
    color: Colors.light.hereworksBlack600,
    paddingHorizontal: 10,
  },
  icon: {
    position: 'absolute',
    bottom: -14,
    end: -28,
    alignItems: 'center',
    justifyContent: 'center',
    height: 24,
    width: 24,
    borderColor: Colors.light.hereworksBlue100,
    borderWidth: 1,
    borderRadius: 8,
    backgroundColor: Colors.light.hereworksWhite100,
  },
  tickIcon: {
    end: -54,
  },
  deleteIcon: {
    end: -80,
    backgroundColor: Colors.light.hereworksRed50,
  },
  shadow: {
    shadowOpacity: 0.5,
    shadowRadius: 2,
    shadowColor: 'black',
    elevation: 5,
  },
  pir: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1,
    padding: 6,
    backgroundColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff50',
  },
  detailsButton: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    flexDirection: 'row',
  },
  sensorActionRow: {
    flexDirection: 'row',
    columnGap: 8,
    marginTop: 8,
  },
  sensorActionButton: {
    flex: 1,
    marginTop: 0,
  },
  detailsButtonTitle: {
    fontFamily: 'Poppins_500Medium',
    paddingVertical: 8,
    color: Colors.light.hereworksBlue500,
  },
  detailHeading: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    lineHeight: 20,
    marginTop: 8,
    marginBottom: 4,
    color: Colors.light.hereworksBlack500,
  },
  timeText: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 12,
    color: Colors.light.hereworksBlack500,
    flex: 1,
    textAlign: 'right',
  },
  sensorInfo: {
    backgroundColor: '#fff',
    width: isMobile ? '100%' : 280,
    marginHorizontal: isMobile ? 8 : 0,
    padding: isMobile ? 32 : 16,
    borderRadius: 10,
    zIndex: 70,
  },
  pirBox: {
    backgroundColor: Colors.light.hereworksWhite400,
    borderColor: Colors.light.hereworksBlue100,
    borderWidth: 1,
    padding: 8,
    marginBottom: isMobile ? 20 : 0,
  },
  menuText: {
    fontFamily: 'Poppins_400Regular',
    paddingHorizontal: 12,
    paddingVertical: 9,
    color: Colors.light.hereworksBlack600,
    lineHeight: 21,
  },
});

export default Sensor;
